export type PlanStoreyElement = {
  storey?: string | number
  positionPlanSettings: {
    planStoreyPolicy: PlanStoreyPolicy
    storeysForPlans: Array<string | number>
  }
}

/** The model store types walls by their placement. */
const WALL_ELEMENT_TYPES: ReadonlySet<string> = new Set(['inner_walls', 'outer_walls'])

const PLAN_STOREY_ELEMENT_TYPES: ReadonlySet<string> = new Set([
  'vertical_slabs',
  'vertical_roof_slabs',
  'beams',
  'purlins',
  'columns',
  'rips',
  'lintels',
  ...WALL_ELEMENT_TYPES,
])

/** Element types with stored plan settings (`positionPlanSettings`). */
export const isPlanStoreyElementType = (type: string | undefined): boolean =>
  type !== undefined && PLAN_STOREY_ELEMENT_TYPES.has(type)

export const isPlanStoreyElement = (element: object): element is PlanStoreyElement =>
  'element_type' in element &&
  isPlanStoreyElementType((element as { element_type?: string }).element_type)

/**
 * Whether the preview of a plan draws `element` on its plan storeys. Walls follow them on the
 * position plan only: the stiffening plan shows a wall on its own storey, as the export does
 * (`walls_for_stiffening_plan`).
 */
export const usesPlanStoreysOnPlan = (
  element: object,
  isStiffeningPlan: boolean,
): element is PlanStoreyElement =>
  isPlanStoreyElement(element) &&
  !(
    isStiffeningPlan &&
    WALL_ELEMENT_TYPES.has((element as { element_type?: string }).element_type ?? '')
  )

/**
 * Same membership as the backend `PlanElementSettingsMixin.isInPlanStorey`:
 * ElementStorey uses the structural storey; UserDefined uses `storeysForPlans`.
 */
export const isInPlanStorey = (element: PlanStoreyElement, storey: string): boolean => {
  const storeyAsStr = String(storey)
  const settings = element.positionPlanSettings
  const policy = settings.planStoreyPolicy
  if (policy === 'ElementStorey') {
    return element.storey != null && String(element.storey) === storeyAsStr
  }
  if (policy === 'UserDefined') {
    return settings.storeysForPlans.some(planStorey => String(planStorey) === storeyAsStr)
  }
  return false
}

export const isOnVisiblePlanStoreys = (
  element: PlanStoreyElement,
  visibleStoreys: ReadonlySet<string>,
): boolean => [...visibleStoreys].some(visibleStorey => isInPlanStorey(element, visibleStorey))

/** Place plan elements on the floorplan storey so FloorplanMesh draws them there. */
export const withFloorplanStorey = <T extends { storey?: string }>(
  elements: T[],
  floorplanStorey: string,
): T[] =>
  elements.map(element =>
    element.storey === floorplanStorey ? element : { ...element, storey: floorplanStorey },
  )
